/**
 * Generates date range intervals for data visualization
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Object} dataByDate - Object containing data points indexed by date
 * @param {string} metricName - Name of the metric being tracked
 * @returns {Array} Array of data points with dates and values
 */
import dayjs from 'dayjs';

export const generateDateRangeIntervals = (startDate, endDate, dataByDate, metricName) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate).subtract(1, 'day');
    const today = dayjs();
    
    // If end date is in the future, use today as end date
    const effectiveEnd = end.isAfter(today) ? today : end;
    const totalDays = effectiveEnd.diff(start, 'day') + 1;
    
    // If total days is less than or equal to 7, return daily records
    if (totalDays <= 7) {
        const intervals = [];
        let currentDate = start;
        
        while (currentDate.isSameOrBefore(effectiveEnd)) {
            const dateStr = currentDate.format('YYYY-MM-DD');
            const day = currentDate.date();
            const month = currentDate.format('MMM');
            
            let ordinalDay;
            if (day === 1 || day === 21 || day === 31) {
                ordinalDay = `${day}st`;
            } else if (day === 2 || day === 22) {
                ordinalDay = `${day}nd`;
            } else if (day === 3 || day === 23) {
                ordinalDay = `${day}rd`;
            } else {
                ordinalDay = `${day}th`;
            }
            
            intervals.push({
                date: `${ordinalDay} ${month}`,
                value: dataByDate?.find(item => item.date === dateStr)?.value || 0
            });
            
            currentDate = currentDate.add(1, 'day');
        }
        return intervals;
    }
    
    // If total days is less than or equal to 49 (7 weeks), return weekly records
    if (totalDays <= 49) {
        const intervals = [];
        let currentStart = start;
        const totalWeeks = Math.ceil(totalDays / 7);
        
        for (let i = 0; i < totalWeeks; i++) {
            let currentEnd;
            if (i === totalWeeks - 1) {
                currentEnd = effectiveEnd;
            } else {
                currentEnd = currentStart.add(6, 'day');
                if (currentEnd.isAfter(effectiveEnd)) {
                    currentEnd = effectiveEnd;
                }
            }
            
            let intervalValue;
            if (metricName === 'page_fans' || metricName === 'page_follows') {
                const lastDateStr = currentEnd.format('YYYY-MM-DD');
                intervalValue = dataByDate?.find(item => item.date === lastDateStr)?.value || 0;
            } else {
                let intervalTotal = 0;
                let currentDate = currentStart;
                while (currentDate.isSameOrBefore(currentEnd)) {
                    const dateStr = currentDate.format('YYYY-MM-DD');
                    intervalTotal += dataByDate?.find(item => item.date === dateStr)?.value || 0;
                    currentDate = currentDate.add(1, 'day');
                }
                intervalValue = intervalTotal;
            }
            
            const weekNumber = i + 1;
            let ordinalWeek;
            if (weekNumber === 1) {
                ordinalWeek = '1st';
            } else if (weekNumber === 2) {
                ordinalWeek = '2nd';
            } else if (weekNumber === 3) {
                ordinalWeek = '3rd';
            } else {
                ordinalWeek = `${weekNumber}th`;
            }
            
            intervals.push({
                date: `${ordinalWeek} Week`,
                value: intervalValue
            });
            
            currentStart = currentEnd.add(1, 'day');
            if (currentStart.isAfter(effectiveEnd)) {
                break;
            }
        }
        return intervals;
    }
    
    // For longer periods, return monthly records
    const intervals = [];
    let currentStart = start;
    const totalMonths = Math.ceil(totalDays / 30);
    
    for (let i = 0; i < totalMonths; i++) {
        let currentEnd;
        if (i === totalMonths - 1) {
            currentEnd = effectiveEnd;
        } else {
            currentEnd = currentStart.add(1, 'month').subtract(1, 'day');
            if (currentEnd.isAfter(effectiveEnd)) {
                currentEnd = effectiveEnd;
            }
        }
        
        let intervalValue;
        if (metricName === 'page_fans' || metricName === 'page_follows') {
            const lastDateStr = currentEnd.format('YYYY-MM-DD');
            intervalValue = dataByDate?.find(item => item.date === lastDateStr)?.value || 0;
        } else {
            let intervalTotal = 0;
            let currentDate = currentStart;
            while (currentDate.isSameOrBefore(currentEnd)) {
                const dateStr = currentDate.format('YYYY-MM-DD');
                intervalTotal += dataByDate?.find(item => item.date === dateStr)?.value || 0;
                currentDate = currentDate.add(1, 'day');
            }
            intervalValue = intervalTotal;
        }
        
        const monthNumber = i + 1;
        let ordinalMonth;
        if (monthNumber === 1) {
            ordinalMonth = '1st';
        } else if (monthNumber === 2) {
            ordinalMonth = '2nd';
        } else if (monthNumber === 3) {
            ordinalMonth = '3rd';
        } else {
            ordinalMonth = `${monthNumber}th`;
        }
        
        intervals.push({
            date: `${ordinalMonth} Month`,
            value: intervalValue
        });
        
        currentStart = currentEnd.add(1, 'day');
        if (currentStart.isAfter(effectiveEnd)) {
            break;
        }
    }
    
    return intervals;
};
